import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Building2, Users, LogOut, Menu, X, Settings } from 'lucide-react';

const RSS_LOGO = "https://customer-assets.emergentagent.com/job_199c36c2-e07b-4567-84a2-e99d8a5503d6/artifacts/c9vy7sfe_rumpun_silat_sarawak_logo_transparent_1024.png";

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Perguruan', href: '/admin/schools', icon: Building2 },
    { name: 'Semua Ahli', href: '/admin/members', icon: Users },
    { name: 'Tetapan', href: '/admin/settings', icon: Settings },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Top accent */}
      <div className="h-0.5 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920] flex-shrink-0" />

      {/* Logo */}
      <div className="p-5 border-b border-[#E5E5E5] flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src={RSS_LOGO} alt="RSS" className="h-9 w-9 object-contain flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-[#0B0B0B] leading-tight truncate">Rumpun Silat Sarawak</div>
            <div className="text-[10px] text-[#D71920] font-semibold tracking-widest mt-0.5">PORTAL PENTADBIRAN</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-[#0B0B0B] text-white'
                  : 'text-[#555] hover:bg-[#F7F7F7] hover:text-[#0B0B0B]'
              }`}
              data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.name}</span>
              {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#D71920]" />}
            </Link>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-[#E5E5E5] flex-shrink-0">
        <div className="mb-3 px-1">
          <p className="text-xs font-semibold text-[#0B0B0B] truncate">{user?.name || user?.email}</p>
          <p className="text-xs text-[#999] truncate">{user?.email}</p>
          <span className="inline-block mt-1.5 px-2 py-0.5 text-[10px] font-bold rounded bg-[#D71920]/10 text-[#D71920] border border-[#D71920]/20 tracking-wide uppercase">
            {user?.role}
          </span>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="w-full justify-start text-xs border-[#E5E5E5] text-[#555] hover:bg-[#FEF2F2] hover:text-[#D71920] hover:border-[#D71920]/30"
          data-testid="logout-button"
        >
          <LogOut className="w-3.5 h-3.5 mr-2" />
          Log Keluar
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F7F7]" style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif" }}>

      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E5E5] h-14 flex items-center px-4 gap-3">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded text-[#555] hover:bg-[#F7F7F7] hover:text-[#0B0B0B] transition-colors"
          data-testid="mobile-sidebar-toggle"
        >
          <Menu size={20} />
        </button>
        <img src={RSS_LOGO} alt="RSS" className="h-7 w-7 object-contain" />
        <span className="text-sm font-bold text-[#0B0B0B]">Rumpun Silat Sarawak</span>
        <span className="ml-auto text-[10px] font-bold text-[#D71920] uppercase tracking-widest">Admin</span>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-64 z-50 bg-white border-r border-[#E5E5E5] transition-transform duration-200 ease-in-out md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Mobile close button */}
        <button
          className="md:hidden absolute top-3 right-3 p-1.5 rounded text-[#555] hover:bg-[#F7F7F7]"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={16} />
        </button>
        <SidebarContent />
      </div>

      {/* Main Content */}
      <div className="md:ml-64 pt-14 md:pt-0 min-h-screen overflow-x-hidden">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
